import React, { useState, useContext, useEffect, useCallback } from 'react';
import { UserContext } from '../UserContext';
import axios from '../axiosInstance';
import { FaInfoCircle, FaExclamationTriangle, FaPlus, FaFileImport } from 'react-icons/fa';
import '../Pages.css';
import '../Pips/Pips.css';   // shared design system (stats-grid, alert-banner, …)
import './TM.css';

const DIRECTIONS = [
  { v: 'credit',        label: 'Credit (incoming)' },
  { v: 'debit',         label: 'Debit (outgoing)' },
  { v: 'transfer_in',   label: 'Transfer in' },
  { v: 'transfer_out',  label: 'Transfer out' }
];
const CHANNELS = ['cash','eft','mobile_money','card','wire','internal'];

const fmt = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy}, ${hh}:${mi}`;
};

const money = (amount, currency) => {
  if (amount == null) return '—';
  return `${currency || 'NAD'} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const hasPermission = (user, name) =>
  !!user && (
    user.is_system_admin === true ||
    (Array.isArray(user.permissions) && user.permissions.includes(name))
  );

const customerName = (t) =>
  t.customer_name_individual || t.customer_name_business || `Customer #${t.customer_id}`;

const blankTx = () => ({
  customer_id: '',
  direction: 'credit',
  amount: '',
  currency: 'NAD',
  channel: 'eft',
  external_ref: '',
  counterparty_name: '',
  counterparty_country: '',
  purpose: '',
  notes: '',
  occurred_at: new Date().toISOString().slice(0,16)
});

function ManageTransactions() {
  const { user } = useContext(UserContext);
  const canRead   = hasPermission(user, 'transactions.read');
  const canCreate = hasPermission(user, 'transactions.create');
  const canImport = hasPermission(user, 'transactions.import');

  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ direction: 'all', query: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [formData, setFormData] = useState(blankTx());
  const [creating, setCreating] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  // CSV import state
  const [showImport, setShowImport]   = useState(false);
  const [importFile, setImportFile]   = useState(null);
  const [importing, setImporting]     = useState(false);
  const [importResult, setImportResult] = useState(null);

  const fetchStats = useCallback(async () => {
    try { const r = await axios.get('/transactions/stats'); setStats(r.data); }
    catch (e) { console.error(e.message); }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams();
      if (filter.direction !== 'all') p.set('direction', filter.direction);
      if (filter.query.trim())        p.set('query', filter.query.trim());
      p.set('limit', '500');
      const r = await axios.get(`/transactions?${p.toString()}`);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (!canRead) return;
    fetchStats();
    fetchList();
  }, [canRead, fetchStats, fetchList]);

  const submitImport = async (e) => {
    e.preventDefault();
    if (!importFile) { setError('Choose a CSV file first'); return; }
    setImporting(true); setError(null); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const r = await axios.post('/transactions/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(r.data);
      setImportFile(null);
      fetchList();
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const submitTx = async (e) => {
    e.preventDefault();
    setCreating(true); setError(null); setLastResult(null);
    try {
      const payload = { ...formData };
      payload.customer_id = Number(payload.customer_id);
      payload.amount      = Number(payload.amount);
      payload.occurred_at = payload.occurred_at ? new Date(payload.occurred_at).toISOString() : undefined;
      ['counterparty_country','external_ref','counterparty_name','purpose','notes'].forEach(k => {
        if (!payload[k]) delete payload[k];
      });
      const r = await axios.post('/transactions', payload);
      setLastResult(r.data);
      setFormData(blankTx());
      fetchList();
      fetchStats();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Failed to record transaction');
    } finally {
      setCreating(false);
    }
  };

  if (user && !canRead) {
    return (
      <div className="page-container">
        <div className="alert-banner alert-banner--warning" style={{ marginTop: '2rem' }}>
          <span className="alert-banner__body">
            <FaInfoCircle /> You don't have permission to view Transactions.
            Ask your administrator for the <code>transactions.read</code> permission.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Stat cards */}
      {user && (
        <div className="stats-section">
          {/* Shared stat-card layout (Pips.css): compact cards */}
          <div className="stats-layout__rest">
          <div className="stats-grid">
            <div className="stat-card stat-card--teal">
              <div className="stat-label">Total transactions</div>
              <div className="stat-value stat-value--teal">{stats ? stats.total : '—'}</div>
            </div>
            <div className="stat-card stat-card--blue">
              <div className="stat-label">Last 30 days</div>
              <div className="stat-value stat-value--blue">{stats ? stats.last_30d_count : '—'}</div>
            </div>
            <div className="stat-card stat-card--brown">
              <div className="stat-label">Volume (30d)</div>
              <div className="stat-value">{stats ? money(stats.last_30d_total) : '—'}</div>
            </div>
            <div className="stat-card stat-card--orange">
              <div className="stat-label">Pending TM alerts</div>
              <div className="stat-value stat-value--orange">{stats ? stats.pending_alerts : '—'}</div>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Filters */}
      <section className="form-section" style={{ marginBottom: '1rem' }}>
        <header className="form-section__head">
          <h4 className="form-section__title">Transaction log</h4>
          <p className="form-section__subtitle">Every record is auto-screened by the TM rule engine on creation.</p>
        </header>
        <div className="form-grid-3">
          <div className="form-group">
            <label>Direction</label>
            <select value={filter.direction} onChange={e => setFilter(f => ({ ...f, direction: e.target.value }))}>
              <option value="all">All directions</option>
              {DIRECTIONS.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Search</label>
            <input type="text" placeholder="ref / counterparty / purpose"
                   value={filter.query}
                   onChange={e => setFilter(f => ({ ...f, query: e.target.value }))}
                   onKeyDown={e => { if (e.key === 'Enter') fetchList(); }} />
          </div>
          <div className="form-group">
            <label>&nbsp;</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="export-button" onClick={fetchList} disabled={loading}>
                {loading ? 'Loading…' : 'Apply'}
              </button>
              {canImport && (
                <button className="export-button" onClick={() => { setImportResult(null); setShowImport(true); }}>
                  <FaFileImport /> Import CSV
                </button>
              )}
              {canCreate && (
                <button className="export-button" onClick={() => setShowCreate(true)}>
                  <FaPlus /> Record
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {error && (
        <div className="alert-banner alert-banner--danger" style={{ marginBottom: '1rem' }}>
          <span className="alert-banner__body"><FaInfoCircle /> {error}</span>
        </div>
      )}

      {/* Transactions table */}
      {rows.length === 0 && !loading && (
        <p style={{ color: 'var(--text-secondary,#607d8b)' }}>No transactions match the current filters.</p>
      )}

      {rows.length > 0 && (
        <table className="tx-table">
          <thead>
            <tr>
              <th>Occurred</th>
              <th>Customer</th>
              <th>Direction</th>
              <th>Amount</th>
              <th>Channel</th>
              <th>Counterparty</th>
              <th>Ref</th>
              <th style={{ textAlign: 'right' }}>Alerts</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(t => (
              <tr key={t.id}>
                <td>{fmt(t.occurred_at)}</td>
                <td>{customerName(t)}</td>
                <td>{t.direction}</td>
                <td className={`tx-amount ${t.direction.includes('debit') || t.direction === 'transfer_out' ? 'tx-amount--debit' : 'tx-amount--credit'}`}>
                  {money(t.amount, t.currency)}
                </td>
                <td>{t.channel || '—'}</td>
                <td>
                  {t.counterparty_name || '—'}
                  {t.counterparty_country && <span style={{ marginLeft: '0.35rem', color: 'var(--text-secondary)', fontSize: '0.72rem' }}>({t.counterparty_country})</span>}
                </td>
                <td>{t.external_ref || '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  {t.alert_count > 0 ? (
                    <span className="tm-pill tm-pill--sev-high">
                      <FaExclamationTriangle /> {t.alert_count}
                    </span>
                  ) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Record transaction modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => !creating && setShowCreate(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <button className="modal-close" onClick={() => !creating && setShowCreate(false)}>×</button>
            <h2 style={{ marginTop: 0 }}>Record transaction</h2>

            {lastResult && (
              <div className="alert-banner alert-banner--success" style={{ marginBottom: '1rem' }}>
                <span className="alert-banner__body">
                  <FaInfoCircle /> Transaction #{lastResult.id} recorded.
                  {Array.isArray(lastResult.alerts) && lastResult.alerts.length > 0
                    ? ` ${lastResult.alerts.length} TM alert${lastResult.alerts.length === 1 ? '' : 's'} fired.`
                    : ' No TM rules fired.'}
                </span>
              </div>
            )}

            <form onSubmit={submitTx}>
              <div className="form-grid-2">
                <div className="form-group">
                  <label>Customer ID</label>
                  <input type="number" required min="1"
                         value={formData.customer_id}
                         onChange={e => setFormData(f => ({ ...f, customer_id: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Occurred at</label>
                  <input type="datetime-local"
                         value={formData.occurred_at}
                         onChange={e => setFormData(f => ({ ...f, occurred_at: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Direction</label>
                  <select value={formData.direction}
                          onChange={e => setFormData(f => ({ ...f, direction: e.target.value }))}>
                    {DIRECTIONS.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Channel</label>
                  <select value={formData.channel}
                          onChange={e => setFormData(f => ({ ...f, channel: e.target.value }))}>
                    {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount</label>
                  <input type="number" required min="0.01" step="0.01"
                         value={formData.amount}
                         onChange={e => setFormData(f => ({ ...f, amount: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Currency</label>
                  <input type="text" maxLength="8"
                         value={formData.currency}
                         onChange={e => setFormData(f => ({ ...f, currency: e.target.value.toUpperCase() }))} />
                </div>
                <div className="form-group">
                  <label>External reference</label>
                  <input type="text" maxLength="120"
                         value={formData.external_ref}
                         onChange={e => setFormData(f => ({ ...f, external_ref: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Counterparty name</label>
                  <input type="text" maxLength="255"
                         value={formData.counterparty_name}
                         onChange={e => setFormData(f => ({ ...f, counterparty_name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Counterparty country (ISO-2)</label>
                  <input type="text" maxLength="2"
                         value={formData.counterparty_country}
                         onChange={e => setFormData(f => ({ ...f, counterparty_country: e.target.value.toUpperCase() }))} />
                </div>
                <div className="form-group">
                  <label>Purpose</label>
                  <input type="text" maxLength="120"
                         value={formData.purpose}
                         onChange={e => setFormData(f => ({ ...f, purpose: e.target.value }))} />
                </div>
                <div className="form-group form-group--full">
                  <label>Notes</label>
                  <textarea rows={3}
                            value={formData.notes}
                            onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))} />
                </div>
              </div>

              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn-cancel" disabled={creating} onClick={() => setShowCreate(false)}>
                  Close
                </button>
                <button type="submit" className="export-button" disabled={creating}>
                  {creating ? 'Saving…' : 'Save & screen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSV import modal */}
      {showImport && (
        <div className="modal-overlay" onClick={() => !importing && setShowImport(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <button className="modal-close" onClick={() => !importing && setShowImport(false)}>×</button>
            <h2 style={{ marginTop: 0 }}>Import transactions (CSV)</h2>

            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #607d8b)' }}>
              Each row gets inserted and auto-screened by the TM rule engine.
              Maximum 10,000 rows / 25&nbsp;MB per upload.
            </p>
            <div style={{ background: 'var(--bg-tertiary, #f5f7fa)', padding: '0.6rem 0.8rem', fontSize: '0.78rem',
                          fontFamily: 'monospace', overflowX: 'auto', whiteSpace: 'pre', marginBottom: '0.75rem' }}>
              customer_id,direction,amount,currency,channel,occurred_at,external_ref,counterparty_name,counterparty_country,purpose,notes{'\n'}
              42,credit,15000,NAD,eft,2026-05-22T09:30:00Z,REF123,Acme Ltd,NA,Salary,
            </div>

            {importResult && (
              <div className={`alert-banner alert-banner--${importResult.failed > 0 ? 'warning' : 'success'}`}
                   style={{ marginBottom: '1rem' }}>
                <span className="alert-banner__body">
                  <FaInfoCircle /> Imported {importResult.imported} row{importResult.imported === 1 ? '' : 's'},
                  {importResult.alerts_created} TM alert{importResult.alerts_created === 1 ? '' : 's'} fired,
                  {importResult.failed} failed.
                </span>
              </div>
            )}

            {importResult?.errors?.length > 0 && (
              <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--card-border, #e3e6ea)',
                            padding: '0.5rem', marginBottom: '0.75rem', fontSize: '0.78rem',
                            fontFamily: 'monospace', background: 'var(--bg-tertiary, #f5f7fa)' }}>
                {importResult.errors.map((e, i) => (
                  <div key={i}>
                    {e.line ? `Line ${e.line}: ` : ''}{e.error}
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={submitImport}>
              <div className="form-group">
                <label>CSV file</label>
                <input type="file" accept=".csv,text/csv" required
                       onChange={e => setImportFile(e.target.files?.[0] || null)}
                       disabled={importing} />
              </div>
              <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn-cancel" disabled={importing}
                        onClick={() => setShowImport(false)}>Close</button>
                <button type="submit" className="export-button" disabled={importing || !importFile}>
                  {importing ? 'Importing…' : 'Upload & screen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageTransactions;
